// src/app/modules/demandes/HorsCatalogueRf.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { getAllUsersRequest } from "../../../../../redux/actions/userActions";
import { createDemandeRfBulkRequest } from "../../../../../redux/actions/demandesActions";

import { Users, ArrowRight } from "lucide-react";

const initialCommon = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
};

export default function HorsCatalogueRf({
                                            campagneId,
                                            hideCampagneSelect = false,
                                            onCancel,
                                            onSuccess,
                                        }) {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);

    const campagnesState = useSelector((s) => s.campagnes || {});
    const refState = useSelector((s) => s.paramDomainesThemes || {});
    const { users } = useSelector((s) => s.user);

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = refState.domaines || refState.domainesList || [];
    const themes = refState.themes || refState.themesList || [];

    const [common, setCommon] = useState(initialCommon);
    const [openPick, setOpenPick] = useState(false);
    const [openBulkFields, setOpenBulkFields] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(getAllUsersRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    // campagne forcée depuis la page wrapper
    useEffect(() => {
        if (hideCampagneSelect && campagneId) {
            setCommon((s) => ({ ...s, campagneId: String(campagneId) }));
        }
    }, [hideCampagneSelect, campagneId]);

    // fallback: si select visible
    useEffect(() => {
        if (!hideCampagneSelect && !common.campagneId && campagnes?.length) {
            setCommon((s) => ({ ...s, campagneId: String(campagnes[0].id) }));
        }
    }, [campagnes, common.campagneId, hideCampagneSelect]);

    const themesFiltered = useMemo(() => {
        const did = common.domaineId ? Number(common.domaineId) : null;
        if (!did) return themes || [];
        return (themes || []).filter((t) => Number(t?.domaineId || t?.domaine?.id) === did);
    }, [themes, common.domaineId]);

    const update = (k, v) => setCommon((s) => ({ ...s, [k]: v }));

    const openSelector = () => {
        if (!common.campagneId) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionne une campagne.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        if (!common.action?.trim()) {
            Swal.fire({
                icon: "warning",
                text: "Action / Intitulé est obligatoire.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        setOpenPick(true);
    };

    const onConfirmUsers = (ids) => {
        if (!ids?.length) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionne au moins un utilisateur.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        const unique = Array.from(new Set(ids || []));
        setSelectedUserIds(unique);
        setOpenPick(false);
        setOpenBulkFields(true);
    };

    const selectedUsers = useMemo(() => {
        const set = new Set((selectedUserIds || []).map(String));
        return (users || []).filter((u) => set.has(String(u.id)));
    }, [users, selectedUserIds]);

    const onSubmitBulkFields = (items) => {
        const payload = {
            campagneId: Number(common.campagneId),
            type: "HORS_CATALOGUE",
            domaineId: common.domaineId ? Number(common.domaineId) : null,
            themeId: common.themeId ? Number(common.themeId) : null,
            action: common.action.trim(),
            description: common.description?.trim() || null,
            items,
        };
        dispatch(createDemandeRfBulkRequest(payload));
        setOpenBulkFields(false);
        setSelectedUserIds([]);
        if (onSuccess) onSuccess();
    };

    return (
        <div className={`hc-page hcrf-page ${hideCampagneSelect ? "is-embedded" : ""}`.trim()}>

            {/* En-tête RF (visible seulement si non embarqué) */}
            {!hideCampagneSelect && (
                <div className="hcrf-header">
                    <div>
                        <div className="hcrf-title">Demande hors catalogue</div>
                        <div className="hcrf-subtitle">
                            {currentUser
                                ? <>RF : <b>{currentUser.firstName} {currentUser.lastName}</b></>
                                : "Responsable Formation"}
                        </div>
                    </div>
                </div>
            )}

            <div className="hc-card hcrf-card">

                {/* ── Informations principales ── */}
                <p className="hc-section-label">Informations principales</p>

                <div className="hc-field">
                    <label>Action / Intitulé *</label>
                    <input
                        value={common.action}
                        onChange={(e) => update("action", e.target.value)}
                        placeholder="Ex: Formation spécifique XYZ..."
                    />
                </div>

                <div className="hc-field">
                    <label>Description</label>
                    <textarea
                        value={common.description}
                        onChange={(e) => update("description", e.target.value)}
                        rows={4}
                        placeholder="Décrivez les objectifs et le contexte de cette demande..."
                    />
                </div>

                {/* ── Paramètres ── */}
                <p className="hc-section-label">Paramètres</p>

                <div className="hc-grid-3">
                    {/* Sélecteur campagne si non embarqué */}
                    {!hideCampagneSelect && (
                        <div className="hc-field">
                            <label>Campagne *</label>
                            <select value={common.campagneId} onChange={(e) => update("campagneId", e.target.value)}>
                                <option value="">—</option>
                                {(campagnes || []).map((c) => (
                                    <option key={c.id} value={c.id}>{c.nom || c.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="hc-field">
                        <label>Domaine</label>
                        <select
                            value={common.domaineId}
                            onChange={(e) => {
                                update("domaineId", e.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">—</option>
                            {(domaines || []).map((d) => (
                                <option key={d.id} value={d.id}>{d.nom || d.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Thème</label>
                        <select value={common.themeId} onChange={(e) => update("themeId", e.target.value)}>
                            <option value="">—</option>
                            {(themesFiltered || []).map((t) => (
                                <option key={t.id} value={t.id}>{t.nom || t.name}</option>
                            ))}
                        </select>
                    </div>
                </div>



                {/* ── Actions ── */}
                <div className="hc-actions">
                    {onCancel && (
                        <button className="hc-btn hc-btn--secondary" type="button" onClick={onCancel}>
                            Annuler
                        </button>
                    )}
                    <button
                        className="hc-btn hcrf-btn-primary"
                        type="button"
                        onClick={openSelector}
                        disabled={!common.campagneId}
                    >
                        <Users size={16} />
                        Choisir les collaborateurs
                        <ArrowRight size={16} />
                    </button>
                </div>

                {!common.campagneId && (
                    <div className="demande-hint">⚠️ Sélectionne d'abord une campagne.</div>
                )}
            </div>

            {/* ── Modals ── */}
            <UsersPickerModal
                open={openPick}
                onClose={() => setOpenPick(false)}
                users={users}
                title="Sélectionner des collaborateurs"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openBulkFields}
                onClose={() => setOpenBulkFields(false)}
                users={selectedUsers}
                title="Paramétrage par collaborateur (HC Bulk RF)"
                onSubmit={onSubmitBulkFields}
            />
        </div>
    );
}